// ══════════════════════════════════════════════════════════════════════════════
//  src/components/dashboard/exams/MisExamenesTab.jsx
//  MIS EXÁMENES — EL HISTORIAL COMPLETO, Y SI ESTOY MEJORANDO
//
//  Rob, 27-09-2026: «¿dónde veo mis resultados?… no tenemos una pestaña de mis
//  exámenes para saber los resultados».
//
//  ── LO QUE HABÍA, Y POR QUÉ NO BASTABA ─────────────────────────────────────
//  Una sección «Exámenes Recientes» dentro del panel, que enseñaba una lista
//  plana y crecía sin fin. Servía para ver el último; no para la pregunta que
//  de verdad se hace quien repite un examen: **¿estoy mejorando?**
//
//  Esa pregunta no se contesta con una lista, se contesta AGRUPANDO por examen
//  y comparando el primer intento con el último. Eso es lo que hace esta
//  pantalla, y es todo lo que hace.
//
//  ── NO PIDE NADA NUEVO AL SERVIDOR ─────────────────────────────────────────
//  Usa el mismo `GET /api/v3/exams/recientes` que ya alimenta el panel. Un
//  endpoint nuevo para reagrupar lo que ya viene sería trabajo de servidor para
//  algo que se resuelve contando en el navegador.
// ══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MisExamenesTab.css';

const TIPO = {
  exam: 'Examen de nivel',
  placement: 'Colocación',
  onboarding: 'Diagnóstico de entrada',
};

const ICONO = { exam: '🎓', placement: '🧭', onboarding: '📋' };

function cuando(iso) {
  try {
    return new Date(iso).toLocaleDateString('es-MX', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return ''; }
}

export default function MisExamenesTab() {
  const navegar = useNavigate();
  const [estado, setEstado] = useState('cargando');
  const [lista, setLista] = useState([]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await fetch('/api/v3/exams/recientes', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) throw new Error('no');
        const d = await r.json();
        if (!vivo) return;
        setLista(Array.isArray(d) ? d : (d.examenes || d.items || []));
        setEstado('listo');
      } catch {
        if (vivo) setEstado('error');
      }
    })();
    return () => { vivo = false; };
  }, []);

  // ── AGRUPADO POR EXAMEN, QUE ES DONDE ESTÁ LA RESPUESTA ─────────────────
  //  Una lista plana de nueve filas no dice si mejoraste; tres grupos de tres
  //  intentos, sí. El orden lo da el intento más reciente de cada grupo:
  //  lo que hiciste ayer va primero.
  const grupos = useMemo(() => {
    const por = new Map();
    for (const e of lista) {
      const clave = e.titulo || e.tipo || 'Examen';
      if (!por.has(clave)) por.set(clave, []);
      por.get(clave).push(e);
    }
    return [...por.entries()]
      .map(([titulo, intentos]) => {
        const orden = [...intentos].sort(
          (a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0));
        const conNota = orden.filter((i) => typeof i.score === 'number');
        return {
          titulo,
          tipo: orden[0]?.tipo,
          cefr: orden[0]?.cefr,
          intentos: orden,
          ultima: conNota[0]?.score ?? null,
          primera: conNota.length > 1 ? conNota[conNota.length - 1].score : null,
          mejor: conNota.length ? Math.max(...conNota.map((i) => i.score)) : null,
          fecha: orden[0]?.fecha,
        };
      })
      .sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0));
  }, [lista]);

  if (estado === 'cargando') {
    return <div className="mex-wrap"><p className="mex-vacio">Cargando tus exámenes…</p></div>;
  }

  if (estado === 'error') {
    return (
      <div className="mex-wrap">
        <p className="mex-vacio">No pudimos cargar tus exámenes ahora mismo.</p>
        <button type="button" className="mex-btn" onClick={() => window.location.reload()}>
          Reintentar
        </button>
      </div>
    );
  }

  if (grupos.length === 0) {
    return (
      <div className="mex-wrap">
        <h2 className="mex-titulo">Mis exámenes</h2>
        {/* Sin resultados no se enseña una tabla vacía: se enseña la puerta. */}
        <p className="mex-vacio">
          Todavía no has presentado ninguno. Los exámenes de nivel están en tu
          curso, y puedes repetirlos las veces que quieras.
        </p>
        <button type="button" className="mex-btn" onClick={() => navegar('/examenes')}>
          Ver los exámenes →
        </button>
      </div>
    );
  }

  return (
    <div className="mex-wrap">
      <h2 className="mex-titulo">Mis exámenes</h2>
      <p className="mex-lead">
        Cada examen con todos tus intentos. Repetir no borra nada: el anterior
        se queda, y así ves si mejoraste.
      </p>

      <div className="mex-grupos">
        {grupos.map((g) => {
          const mejoro = g.primera !== null && g.ultima !== null && g.ultima > g.primera;
          const empeoro = g.primera !== null && g.ultima !== null && g.ultima < g.primera;
          return (
            <section className="mex-grupo" key={g.titulo}>
              <header className="mex-grupo-top">
                <span className="mex-icono" aria-hidden="true">{ICONO[g.tipo] || '📝'}</span>
                <div className="mex-grupo-id">
                  <h3 className="mex-grupo-tit">{g.titulo}</h3>
                  <p className="mex-grupo-sub">
                    {TIPO[g.tipo] || 'Examen'}
                    {g.cefr ? ` · nivel ${g.cefr}` : ''}
                    {` · ${g.intentos.length} ${g.intentos.length === 1 ? 'intento' : 'intentos'}`}
                  </p>
                </div>
                {g.mejor !== null && (
                  <span className="mex-mejor" title="Tu mejor nota en este examen">
                    {Math.round(g.mejor)}%
                  </span>
                )}
              </header>

              {/* ── LA RESPUESTA A «¿ESTOY MEJORANDO?» ──────────────────────
                  Sólo aparece si hay con qué compararlo. Con un intento no hay
                  progreso que contar, y fingirlo sería ruido. */}
              {(mejoro || empeoro) && (
                <p className={`mex-progreso ${mejoro ? 'mex-sube' : 'mex-baja'}`}>
                  {mejoro
                    ? `Subiste de ${Math.round(g.primera)}% a ${Math.round(g.ultima)}%`
                    : `Bajaste de ${Math.round(g.primera)}% a ${Math.round(g.ultima)}%`}
                </p>
              )}

              <ol className="mex-intentos">
                {g.intentos.map((i, n) => (
                  <li className="mex-intento" key={i.session_id || n}>
                    <span className="mex-fecha">{cuando(i.fecha)}</span>
                    <span className="mex-nota">
                      {typeof i.score === 'number' ? `${Math.round(i.score)}%` : (i.cefr || '—')}
                    </span>
                    {i.aprobado === true && <span className="mex-tag mex-tag-ok">Aprobado</span>}
                    {i.aprobado === false && <span className="mex-tag mex-tag-no">Reprobado</span>}
                    {i.estado === 'in_progress' && <span className="mex-tag">En curso</span>}
                    {i.session_id && (
                      <button
                        type="button"
                        className="mex-ver"
                        onClick={() => navegar(`/examenes?result=${i.session_id}`)}
                      >
                        Ver →
                      </button>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          );
        })}
      </div>

      <button type="button" className="mex-btn" onClick={() => navegar('/examenes')}>
        Presentar otro examen →
      </button>
    </div>
  );
}
